import React, { useContext, useEffect, useState } from 'react'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog
} from '@clientbase/clientbase-library'
import { Box } from '@mui/material'
import {
  InvoiceTimeline,
  useInvoiceTimeline,
  useResendNotification
} from 'features/invoice-timeline'

import { theme } from 'styles/theme'

import {
  ModalChargeDetailContext,
  NOTIFICATIONS_SUMMARY_ID
} from '../../context'
import { cardTittle, getStatus } from '../../ModalChargeDetail.utils'

/**
 * SC-10890: o accordion deixou de ser uma tabela de envios e virou a timeline da
 * cobrança — envios, aberturas e respostas do cliente em ordem cronológica
 * (mais antigo em cima), com o marco de vencimento no meio do trilho.
 *
 * As tabs Pré/Pós-vencimento saíram. Elas classificavam por STRING do nome da
 * campanha traduzido ('Envio de fatura', 'Lembrete de vencimento', ...), numa
 * lista hardcoded de cinco itens: campanha nova caía silenciosamente em
 * "pós-vencimento" e renomear um rótulo quebrava a aba sem aviso. A posição
 * relativa ao marco dá a mesma leitura de graça.
 */
const NotificationDetails: React.FC = () => {
  const {
    invoice,
    notificationsExpanded,
    updateNotificationsExpanded,
    updateReload
  } = useContext(ModalChargeDetailContext)

  // Só busca quando o accordion é expandido: sem isso, todo detalhe de cobrança
  // aberto no sistema dispararia uma requisição de timeline. Fica ligado depois
  // da primeira abertura para não refazer a busca a cada vez que fecha e abre.
  // A abertura pode vir do clique no accordion ou do scrollToNotifications
  // (botão "Reenviar notificação" e ícone de leitura do header).
  const [hasExpanded, setHasExpanded] = useState(false)
  useEffect(() => {
    if (notificationsExpanded) setHasExpanded(true)
  }, [notificationsExpanded])
  const timeline = useInvoiceTimeline(invoice?.uuid, hasExpanded)

  const resend = useResendNotification(hasExpanded, () => {
    timeline.reload()
    updateReload((prev) => !prev)
  })

  if (getStatus(invoice?.status)?.label === 'Pendente') {
    return null
  }

  return (
    <>
      {/* Sem <div> em volta: os irmãos deste accordion são filhos diretos do
          <Stack gap={1}> do corpo do modal, e um wrapper tirava esta seção desse
          espaçamento. Também não dá para pôr ref no Accordion (a lib não faz
          forwardRef); por isso ele é controlado pelo contexto. */}
      <Accordion
        expanded={notificationsExpanded}
        onChange={(_, expanded) => updateNotificationsExpanded(expanded)}
        sx={{
          border: '1px solid #E8EBE8',
          borderLeft: '3px solid',
          borderLeftColor: 'primary.main',
          borderRadius: '8px !important',
          backgroundColor: '#FAFBFA',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
            backgroundColor: '#fff'
          }
        }}
      >
        <AccordionSummary
          aria-controls="notificationDetails-content"
          id={NOTIFICATIONS_SUMMARY_ID}
        >
          {cardTittle(
            'Notificações e aberturas',
            'notificationImportant',
            `${theme.palette.primary.main}`
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Box
            width="100%"
            sx={{ [theme.breakpoints.down(450)]: { maxWidth: '40vh' } }}
          >
            <InvoiceTimeline
              data={timeline.data}
              loading={timeline.loading}
              error={timeline.error}
              dueDate={invoice?.dueDate}
              onResend={resend.request}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Confirmação de custo do reenvio: a tarifa vem da API a cada abertura, e
          não das capabilities do JWT — o coexistence pode ser ligado ou cair com
          o usuário já logado, e o token só renova no login. */}
      <Dialog
        icon="faBell"
        title={resend.isScheduled ? 'Enviar agora' : 'Reenviar notificação'}
        description={resend.description}
        setIsOpenDialog={resend.cancel}
        isOpenDialog={!!resend.target}
        cancelButton
      >
        <Button
          fullWidth
          variantButton="primary"
          onClick={resend.confirm}
          disabled={resend.sending}
        >
          {resend.isScheduled ? 'Enviar agora' : 'Reenviar'}
        </Button>
      </Dialog>
    </>
  )
}

export default NotificationDetails
